"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCookieConsent = useCookieConsent;
const react_1 = require("react");
const DEFAULT_STORAGE_KEY = 'cookie-consent';
/**
 * Hook to manage cookie consent state with localStorage persistence.
 * Privacy-first: defaults to false during SSR/hydration.
 *
 * @param options - Configuration options
 * @returns Object containing consent state and control functions
 *
 * @example
 * ```tsx
 * function CookieBanner() {
 *   const { hasConsent, hasDecided, acceptCookies, declineCookies } = useCookieConsent({
 *     onAccept: () => console.log('Analytics enabled'),
 *     onDecline: () => console.log('Analytics disabled'),
 *   });
 *
 *   if (hasDecided) return null;
 *
 *   return (
 *     <div>
 *       <button onClick={acceptCookies}>Accept</button>
 *       <button onClick={declineCookies}>Decline</button>
 *     </div>
 *   );
 * }
 * ```
 */
function useCookieConsent(options = {}) {
    const { storageKey = DEFAULT_STORAGE_KEY, onAccept, onDecline } = options;
    const [consentStatus, setConsentStatus] = (0, react_1.useState)(null);
    // Initialize from localStorage after mount (SSR-safe)
    (0, react_1.useEffect)(() => {
        if (typeof window === 'undefined')
            return;
        const stored = localStorage.getItem(storageKey);
        if (stored === 'accepted' || stored === 'declined') {
            setConsentStatus(stored);
        }
    }, [storageKey]);
    const acceptCookies = (0, react_1.useCallback)(() => {
        if (typeof window === 'undefined')
            return;
        localStorage.setItem(storageKey, 'accepted');
        setConsentStatus('accepted');
        onAccept?.();
    }, [storageKey, onAccept]);
    const declineCookies = (0, react_1.useCallback)(() => {
        if (typeof window === 'undefined')
            return;
        localStorage.setItem(storageKey, 'declined');
        setConsentStatus('declined');
        onDecline?.();
    }, [storageKey, onDecline]);
    const resetConsent = (0, react_1.useCallback)(() => {
        if (typeof window === 'undefined')
            return;
        localStorage.removeItem(storageKey);
        setConsentStatus(null);
    }, [storageKey]);
    return {
        hasConsent: consentStatus === 'accepted',
        consentStatus,
        acceptCookies,
        declineCookies,
        resetConsent,
        hasDecided: consentStatus !== null,
    };
}
